import { tavus } from "./client";
import type {
  ScenarioSourceItem,
  TavusKnowledgeDocument,
} from "@/lib/powtoon-be/types";
import {
  sourceKeyOf,
  sourceLabelOf,
  documentCandidates,
  resolveSourceDownloadUrl,
} from "../sourceKeys";
import { createLogger } from "@/lib/frontend-logger";

export { sourceKeyOf, sourceLabelOf, documentCandidates };

const logger = createLogger("tavus.knowledgeDocuments");

export const TAVUS_PRESENTATION_SKILL_ID = "presentation";

/**
 * Tavus only accepts letters, numbers, underscores and hyphens in document
 * tags — a colon (as in our internal `scenario:<id>` keys) is rejected with
 * a 400. Tags are dashboard metadata only; documents are always attached by
 * explicit `document_id`.
 */
export function tavusTag(scenarioId: string): string {
  return `scenario_${scenarioId}`.replace(/[^A-Za-z0-9_-]/g, "-");
}

/**
 * Register one scenario source with the Tavus knowledge base. Called from
 * the editor (explicit "Add to Tavus"), never from the save pipeline — the
 * trainer sees the outcome before committing the scenario.
 */
export async function registerSourceDocument(input: {
  item: ScenarioSourceItem;
  scenarioId?: string;
}): Promise<TavusKnowledgeDocument> {
  const { item, scenarioId } = input;
  const key = sourceKeyOf(item);
  if (!key) {
    throw new Error("This source can't be used as a Tavus knowledge document.");
  }
  const documentUrl = await resolveSourceDownloadUrl(item, scenarioId);
  const created = await tavus.createDocument({
    document_url: documentUrl,
    document_name: sourceLabelOf(item).slice(0, 120),
    tags: scenarioId ? [tavusTag(scenarioId)] : undefined,
  });
  logger.info("tavus document registered", {
    scenario_id: scenarioId,
    source_key: key,
    document_id: created.document_id,
    status: created.status,
    error_message: created.error_message ?? null,
    document_url_host: (() => {
      try {
        return new URL(created.document_url ?? documentUrl).host;
      } catch {
        return "invalid-url";
      }
    })(),
  });
  return {
    source_key: key,
    document_id: created.document_id,
    document_name: created.document_name ?? sourceLabelOf(item),
    document_url: created.document_url ?? documentUrl,
    status: (created.status as TavusKnowledgeDocument["status"]) ?? "started",
    error_message: created.error_message ?? undefined,
    synced_at: new Date().toISOString(),
  };
}

/**
 * Live status for entries stored on the scenario. Nothing is created or
 * deleted here: an entry Tavus no longer has comes back as `missing` so the
 * editor can offer to register its source again.
 */
export async function verifyDocuments(
  entries: TavusKnowledgeDocument[],
): Promise<TavusKnowledgeDocument[]> {
  return await Promise.all(
    entries.map(async (entry) => {
      try {
        const doc = await tavus.getDocument(entry.document_id);
        if (doc.missing) return { ...entry, status: "missing" as const };
        return {
          ...entry,
          document_name: doc.document_name ?? entry.document_name,
          status: (doc.status as TavusKnowledgeDocument["status"]) ?? entry.status,
          error_message: doc.error_message ?? undefined,
          synced_at: new Date().toISOString(),
        };
      } catch (err) {
        logger.warn("could not verify tavus document", {
          document_id: entry.document_id,
          error: (err as Error)?.message,
        });
        return entry;
      }
    }),
  );
}

/**
 * Save-time reconciliation. Documents are already registered by the editor,
 * so this only decides what to keep on the scenario and what to retire:
 * entries whose source was removed from the scenario are dropped and their
 * upstream documents deleted after the scenario is persisted.
 */
export function reconcileSavedDocuments(input: {
  /** Entries the editor hands up (already registered upstream). */
  entries: TavusKnowledgeDocument[];
  /** `source_key` the trainer selected for retrieval, or undefined. */
  selectedSourceKey?: string;
  inputs: ScenarioSourceItem[];
}): {
  knowledge_documents: TavusKnowledgeDocument[];
  knowledge_document_id?: string;
  retiredDocumentIds: string[];
} {
  const { entries, selectedSourceKey, inputs } = input;
  const liveKeys = new Set(documentCandidates(inputs).map((c) => c.key));
  const kept = entries.filter(
    (e) => liveKeys.has(e.source_key) && e.status !== "missing",
  );
  const retiredDocumentIds = entries
    .filter((e) => !kept.some((k) => k.document_id === e.document_id))
    .filter((e) => e.status !== "missing")
    .map((e) => e.document_id);
  const active = selectedSourceKey
    ? kept.find((e) => e.source_key === selectedSourceKey)
    : undefined;
  return {
    knowledge_documents: kept,
    knowledge_document_id: active?.document_id,
    retiredDocumentIds,
  };
}

/** Delete retired documents. Failures are logged, never fatal. */
export async function deleteTavusDocuments(documentIds: string[]): Promise<void> {
  await Promise.all(
    documentIds.map(async (id) => {
      try {
        await tavus.deleteDocument(id);
      } catch (err) {
        logger.warn("could not delete tavus document", {
          document_id: id,
          error: (err as Error)?.message,
        });
      }
    }),
  );
}

/** Terminal statuses — nothing left to poll for. */
export function isTerminalDocumentStatus(
  status?: TavusKnowledgeDocument["status"],
): boolean {
  return status === "ready" || status === "error";
}

/**
 * Fresh status straight from Tavus. The status stored on the scenario is a
 * snapshot from the last save and goes stale while Tavus indexes, so anything
 * that gates on `ready` (the presentation skill) must re-check here.
 */
export async function refreshDocumentStatus(documentId: string): Promise<{
  status: TavusKnowledgeDocument["status"];
  error_message?: string;
}> {
  const doc = await tavus.getDocument(documentId);
  return {
    status: (doc.status as TavusKnowledgeDocument["status"]) ?? "processing",
    error_message: doc.error_message ?? undefined,
  };
}

/**
 * Actual state of the presentation skill on the pal. The scenario stores the
 * trainer's *intent*; Tavus owns the *actual* attachment, so anything that
 * reports or reconciles the two must read it from here.
 */
export async function getPresentationSkillState(palId: string): Promise<{
  attached: boolean;
  documentIds: string[];
}> {
  const res = await tavus.getPalSkill({
    pal_id: palId,
    skill_id: TAVUS_PRESENTATION_SKILL_ID,
  });
  const ids = (res.config?.document_ids as string[] | undefined) ?? [];
  return { attached: !!res.attached, documentIds: Array.isArray(ids) ? ids : [] };
}

/** Attach the presentation skill for one ready document. */
export async function attachPresentationSkill(input: {
  palId: string;
  documentId: string;
  slidesTrigger?: "on_demand" | "automatic";
}): Promise<void> {
  await tavus.attachSkill({
    pal_id: input.palId,
    skill_id: TAVUS_PRESENTATION_SKILL_ID,
    config: {
      document_ids: [input.documentId],
      slides_trigger: input.slidesTrigger ?? "on_demand",
    },
  });
}